import { use, useCallback, useEffect, useState } from 'react';
import { Button, Col, Form, Row, Spinner } from 'react-bootstrap';
import { useTranslation } from "react-i18next";

import useAlert from '../../Alerts/useAlert';
import MultiselectBox from '../../Components/MultiselectBox';
import { UserPreferencesContext } from '../../Contexts/UserPreferencesContext';
import DateRange from '../../DateRange';
import { useBusinessUnits } from '../../Utilities/useBusinessUnits';
import { useHttpUtils } from '../../Utilities/useHttpUtils';
import { useLines } from '../../Utilities/useLines';
import ReportingLotListTable from './ReportingLotListTable';

function LotsTable() {

    const { t } = useTranslation();
    const { fetchBlobAuthenticated } = useHttpUtils();

    const getFromDate = () => {
        // Today
        let fromDate = new Date();

        // Last day of the previous month
        fromDate.setDate(0);

        // First day of previous month after setting the date to the last day of the previous month
        fromDate.setDate(1);

        // Unused but good to know.. 30 days ago...
        // todayDate.setDate(todayDate.getDate() - 30);

        return fromDate.toISOString().split('T')[0];
    };

    const getToDate = () => {
        // Today
        let toDate = new Date();

        // Last day of the previous month
        toDate.setDate(0);

        return toDate.toISOString().split('T')[0];
    };

    const defaultDateRange = {
        from: getFromDate(),
        to: getToDate()
    };

    const [loadingLotData, setLoadingLotData] = useState(false);
    const [loadingFilters, setLoadingFilters] = useState(true);
    const [loadingSelectedBusinessUnits, setLoadingSelectedBusinessUnits] = useState(true);
    const [loadingSelectedLines, setLoadingSelectedLines] = useState(true);
    const { loading: loadingBusinessUnits, businessUnits } = useBusinessUnits();
    const { loading: loadingUserPreferences, userPreferences } = use(UserPreferencesContext);
    const [selectedBusinessUnits, setSelectedBusinessUnits] = useState([]);

    const { loading: loadingLines, lines } = useLines(selectedBusinessUnits);
    const [selectedLines, setSelectedLines] = useState([]);

    const [dateFilter, setDateFilter] = useState(defaultDateRange);
    const [formValidated, setFormValidated] = useState(false);

    useEffect(() => {
        setLoadingSelectedBusinessUnits(true);
        setSelectedBusinessUnits(businessUnits.filter(bu => userPreferences?.lotList?.defaultBusinessUnits?.some(code => code === bu.code)));
        setLoadingSelectedBusinessUnits(false);
    }, [businessUnits, userPreferences?.lotList?.defaultBusinessUnits]);

    useEffect(() => {
        setLoadingSelectedLines(true);
        setSelectedLines(lines.filter(l => userPreferences?.lotList?.defaultLines?.some(line => line === l.productionLine)));
        setLoadingSelectedLines(false);
    }, [lines, userPreferences?.lotList?.defaultLines]);

    useEffect(() => {
        setLoadingFilters(loadingBusinessUnits || loadingUserPreferences || loadingLines || loadingSelectedBusinessUnits || loadingSelectedLines);
    }, [loadingBusinessUnits, loadingLines, loadingSelectedBusinessUnits, loadingSelectedLines, loadingUserPreferences]);

    const handleBusinessUnitChange = useCallback((businessUnits) => {
        setLoadingFilters(true);
        setSelectedBusinessUnits(businessUnits);
    }, []);

    const { setAlert } = useAlert();

    const [filters, setFilters] = useState([]);

    const exportLotData = async () => {
        setLoadingLotData(true);

        let searchParams = new URLSearchParams();
        if (dateFilter?.from) {
            searchParams.append('dateFrom', dateFilter?.from)
        }

        if (dateFilter?.to) {
            searchParams.append('dateTo', dateFilter?.to)
        }

        if (selectedBusinessUnits?.length) {
            searchParams.append('businessUnits', selectedBusinessUnits?.map(bu => bu.code).join(","))
        }

        if (selectedLines?.length) {
            searchParams.append('lines', selectedLines?.map(l => l.productionLine).join(","))
        }

        let businessUnitFilter = filters.find(x => x.id === "businessUnit")?.value;
        if (businessUnitFilter) {
            searchParams.append('businessUnit', businessUnitFilter);
        }

        let lotNumberFilter = filters.find(x => x.id === "lotNumber")?.value;
        if (lotNumberFilter) {
            searchParams.append('lotNumber', lotNumberFilter);
        }

        let gradeFilter = filters.find(x => x.id === "grade")?.value;
        if (gradeFilter) {
            searchParams.append('grade', gradeFilter);
        }

        let lastModifiedByFilter = filters.find(x => x.id === "lastModifiedBy")?.value;
        if (lastModifiedByFilter) {
            searchParams.append('lastModifiedBy', lastModifiedByFilter);
        }

        let lastCommentFilter = filters.find(x => x.id === "lastComment")?.value;
        if (lastCommentFilter) {
            searchParams.append('lastComment', lastCommentFilter);
        }

        let lastGradedReasonCodeFilter = filters.find(x => x.id === "lastGradedReasonCode")?.value;
        if (lastGradedReasonCodeFilter) {
            searchParams.append("lastGradedReasonCode", lastGradedReasonCodeFilter);
        }

        let url = 'api/v1/reporting/lotData?' + searchParams.toString();
        await fetchBlobAuthenticated(url)
            .then(async response => {
                if (response.ok) {
                    return response.blob();
                } else {
                    throw new Error(await response.text());
                }
            })
            .then((response) => {
                return response = response.slice(0, response.size, "application/csv");
            })
            .then(blob => {
                setLoadingLotData(false);
                // Does the filename need to be unique?
                downloadFile(blob, `LotData.csv`);
            })
            .catch(error => {
                setLoadingLotData(false);
                setAlert("danger", "Error", error.message);
            });
    };

    const downloadFile = (blob, fileName) => {
        const a = document.createElement('a');
        a.download = fileName;
        a.href = window.URL.createObjectURL(blob);
        const clickEvt = new MouseEvent('click',
            {
                view: window,
                bubbles: true,
                cancelable: true,
            });
        a.dispatchEvent(clickEvt);
        a.remove();
    };

    const handleSubmit = (event) => {

        event.preventDefault();
        event.stopPropagation();

        setFormValidated(true);

        const form = event.currentTarget;

        if (form.checkValidity() === true) {
            // produce report
            exportLotData();
        }
    };

    return (
        <>
            <Form id="reportingLotListForm" noValidate validated={formValidated} onSubmit={handleSubmit}>
                <Row className="mb-3 gy-2 d-flex align-items-bottom">
                    <Col md>
                        <DateRange dateFilter={dateFilter} setDateFilter={setDateFilter} />
                    </Col>
                    <Col md>
                        <MultiselectBox
                            id={"multiselect-businessUnits"}
                            name={t("LotList.Filters.Business")}
                            options={businessUnits}
                            selectedOptions={selectedBusinessUnits}
                            setSelectedOptions={handleBusinessUnitChange}
                            map="code" />
                    </Col>
                    <Col md>
                        <MultiselectBox
                            id={"multiselect-lines"}
                            name={t("LotList.Filters.Line")}
                            options={lines}
                            selectedOptions={selectedLines}
                            setSelectedOptions={setSelectedLines}
                            map="productionLine" />
                    </Col>
                    <Col md>
                        <div className="d-flex justify-content-end">
                            <Button variant="primary" type="submit" form="reportingLotListForm" disabled={loadingLotData}>
                                {t("Reporting.ExportLotData")}
                                {loadingLotData &&
                                    <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                        className="ms-2"
                                    />
                                }
                            </Button>
                        </div>
                    </Col>
                </Row>
            </Form>

            <ReportingLotListTable tableName="lots-reporting-table" tableFilters={{ selectedBusinessUnits: selectedBusinessUnits, selectedLines: selectedLines, dateFilter: dateFilter }} loadingFilters={loadingFilters} setFilters={setFilters} />
        </>
    );
}

export default LotsTable;